import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef, useState } from "react";
/**
 * A menu button and its floating panel (design-spec §4 "Menu", "Avatar menu"): items 16px with
 * an optional icon, a hairline rule, on `--float` with `--shadow-menu` and `--radius-panel`,
 * opening below right, min 220. Only menus and dialogs float (§1). No accent on an item (§4
 * "Accent on menu items" is a Don't); hover fills `--tone` through `--menu-item-bg`, set by
 * `interactions.css`.
 *
 * Keyboard (e2e `shell.spec.ts`): Enter or Space on the trigger opens it and focuses the first
 * item; ArrowDown and ArrowUp move with wrap, Home and End jump; Tab walks the items in order;
 * Escape closes and returns focus to the trigger. A click outside, or focus leaving both the
 * trigger and the panel, closes it.
 */
export function Menu({ items, label, trigger }) {
    const [open, setOpen] = useState(false);
    // Which item takes focus once the panel has rendered: set by whatever opened it.
    const [focusOnOpen, setFocusOnOpen] = useState("first");
    const id = useId();
    const wrapper = useRef(null);
    const button = useRef(null);
    const panel = useRef(null);
    const entries = () => Array.from(panel.current?.querySelectorAll('[role="menuitem"]') ?? []);
    const close = (returnFocus) => {
        setOpen(false);
        if (returnFocus)
            button.current?.focus();
    };
    useEffect(() => {
        if (!open)
            return;
        const list = entries();
        (focusOnOpen === "first" ? list[0] : list[list.length - 1])?.focus();
        const outside = (event) => {
            if (!wrapper.current?.contains(event.target))
                setOpen(false);
        };
        document.addEventListener("pointerdown", outside);
        return () => document.removeEventListener("pointerdown", outside);
        // Keyed on `open` alone: focus moves once per opening, not on every render while open.
    }, [open]);
    const openAt = (where) => {
        setFocusOnOpen(where);
        setOpen(true);
    };
    const onTriggerKey = (event) => {
        if (event.key === "ArrowDown" || event.key === "ArrowUp") {
            event.preventDefault();
            openAt(event.key === "ArrowDown" ? "first" : "last");
        }
        else if (event.key === "Escape" && open) {
            event.preventDefault();
            close(true);
        }
    };
    const onPanelKey = (event) => {
        const list = entries();
        const at = list.indexOf(document.activeElement);
        const move = (index) => {
            event.preventDefault();
            list[(index + list.length) % list.length]?.focus();
        };
        if (event.key === "ArrowDown")
            move(at + 1);
        else if (event.key === "ArrowUp")
            move(at < 0 ? list.length - 1 : at - 1);
        else if (event.key === "Home")
            move(0);
        else if (event.key === "End")
            move(list.length - 1);
        else if (event.key === "Escape") {
            event.preventDefault();
            event.stopPropagation();
            close(true);
        }
    };
    return (_jsxs("div", { ref: wrapper, "data-menu-root": "", style: { position: "relative", display: "flex", alignItems: "center" }, onBlur: (event) => {
            const next = event.relatedTarget;
            if (open && next !== null && !wrapper.current?.contains(next))
                setOpen(false);
        }, children: [trigger({
                ref: button,
                "aria-haspopup": "menu",
                "aria-expanded": open,
                "aria-controls": open ? id : undefined,
                onClick: () => (open ? close(false) : openAt("first")),
                onKeyDown: onTriggerKey,
            }, open), open && (_jsx("div", { ref: panel, id: id, role: "menu", "aria-label": label, "data-menu": "", onKeyDown: onPanelKey, style: {
                    position: "absolute", top: "calc(100% + var(--space-075))", right: 0, zIndex: 20,
                    minWidth: 220, padding: "var(--space-075)", display: "flex", flexDirection: "column",
                    background: "var(--float)", borderRadius: "var(--radius-panel)",
                    boxShadow: "var(--shadow-menu)",
                }, children: items.map((item) => (_jsxs("div", { style: { display: "contents" }, children: [item.ruleBefore && (_jsx("hr", { "aria-hidden": "true", style: {
                                border: "none", height: "var(--border-hairline)", background: "var(--line)",
                                margin: "var(--space-075) var(--space-05)",
                            } })), _jsxs("button", { type: "button", role: "menuitem", "data-menu-item": item.id, onClick: () => {
                                close(true);
                                item.onSelect();
                            }, style: {
                                display: "flex", alignItems: "center", gap: 10, width: "100%",
                                padding: "var(--space-1) 10px", margin: 0, border: "none",
                                borderRadius: "var(--radius-item)", background: "var(--menu-item-bg, transparent)",
                                color: "var(--ink-1)", cursor: "pointer", textAlign: "left", whiteSpace: "nowrap",
                                fontFamily: "var(--font-sans)", fontSize: "var(--type-body-size)",
                                lineHeight: "var(--type-body-line)", fontWeight: "var(--type-body-weight)",
                            }, children: [item.icon, item.label] })] }, item.id))) }))] }));
}
